/*
 * Generated by tutor-mfe for all.
 *
 * Frontend-base counterpart of the legacy env.config.jsx.
 */

import React, { useEffect, useRef, useState } from 'react';
import Cookies from 'universal-cookie';

import { getConfig } from '@edx/frontend-platform';
import { Icon } from '@openedx/paragon';
import { Nightlight, WbSunny } from '@openedx/paragon/icons';
import { useIntl } from '@edx/frontend-platform/i18n';

import { DIRECT_PLUGIN, IFRAME_PLUGIN, PLUGIN_OPERATIONS } from '@openedx/frontend-plugin-framework';

function addPlugins(config, slot_name, plugins) {
  if (slot_name in config.pluginSlots === false) {
    config.pluginSlots[slot_name] = {
      keepDefault: true,
      plugins: []
    };
  }
  config.pluginSlots[slot_name].plugins.push(...plugins);
}

function setConfig() {
  const config = {
    pluginSlots: {},
  };
// Language dropdown used by the MFEs that keep the upstream header (Studio, account, profile...).
// One cookie is shared by the LMS, Studio and every MFE (see lumora-lang.js in the theme).
const LanguageSwitcher = () => {
  const config = getConfig();
  const intl = useIntl();
  const current = (intl.locale || 'vi').slice(0, 2) === 'en' ? 'en' : 'vi';
  const labels = { vi: 'Tiếng Việt', en: 'English' };
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) { return undefined; }
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) { setOpen(false); } };
    const onKey = (e) => { if (e.key === 'Escape') { setOpen(false); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const change = (code) => {
    setOpen(false);
    if (code === current) { return; }
    const name = config.LANGUAGE_PREFERENCE_COOKIE_NAME || 'openedx-language-preference';
    const domain = config.SESSION_COOKIE_DOMAIN || window.location.hostname;
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${code}; domain=${domain}; path=/; max-age=31536000; SameSite=Lax${secure}`;
    window.location.reload();
  };

  return (
    <div className="lm-menu lm-lang" ref={boxRef}>
      <button type="button" className="lm-menu__btn" aria-haspopup="listbox" aria-expanded={open} aria-label="Language / Ngôn ngữ" onClick={() => setOpen(!open)}>
        <span className={`lm-flag lm-flag--${current}`} aria-hidden="true" />
        <span className="lm-menu__label">{labels[current]}</span>
        <svg className="lm-menu__chev" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
      </button>
      {open && (
        <ul className="lm-menu__list" role="listbox" aria-label="Language / Ngôn ngữ">
          {['vi', 'en'].map((code) => (
            <li key={code} role="presentation">
              <button type="button" role="option" aria-selected={code === current} className={`lm-menu__item${code === current ? ' is-active' : ''}`} onClick={() => change(code)}>
                <span className={`lm-flag lm-flag--${code}`} aria-hidden="true" />
                <span>{labels[code]}</span>
                {code === current && <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7" /></svg>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
const LumoraFooter = () => {
  const intl = useIntl();
  const config = getConfig();
  const lang = (intl.locale || 'vi').slice(0, 2) === 'en' ? 'en' : 'vi';
  const text = {
    vi: { tagline: 'Học tập, được soi sáng.', rights: 'Bảo lưu mọi quyền.' },
    en: { tagline: 'Learning, illuminated.', rights: 'All rights reserved.' },
  }[lang];
  const links = [
    ['/about', { vi: 'Giới thiệu', en: 'About us' }],
    ['/tos', { vi: 'Điều khoản', en: 'Terms of Service' }],
    ['/privacy', { vi: 'Quyền riêng tư', en: 'Privacy Policy' }],
    ['/help', { vi: 'Trợ giúp', en: 'Help' }],
    ['/contact', { vi: 'Liên hệ', en: 'Contact us' }],
  ];
  const theming = `${config.LMS_BASE_URL}/theming/asset/images`;

  return (
    <div className="wrapper wrapper-footer">
      <style>
        {`
          #root .lumora-footer-logo-dark { display: none; }
          [data-paragon-theme-variant="dark"] #root .lumora-footer-logo-light { display: none; }
          [data-paragon-theme-variant="dark"] #root .lumora-footer-logo-dark { display: inline-block; }
        `}
      </style>
      <footer id="footer" className="tutor-container">
        <div className="footer-top">
          <div className="powered-area">
            <ul className="logo-list">
              <li>
                <a href={config.LMS_BASE_URL} aria-label="Lumora">
                  <img className="lumora-footer-logo-light" src={`${theming}/logo.png`} alt="Lumora" height="36" />
                  <img className="lumora-footer-logo-dark" src={`${theming}/logo-white.png`} alt="" height="36" />
                </a>
              </li>
              <li className="footer-tagline">{text.tagline}</li>
            </ul>
          </div>
          <nav className="nav-colophon">
            <ol>
              {links.map(([url, titles]) => (
                <li key={url}><a href={`${config.LMS_BASE_URL}${url}`}>{titles[lang]}</a></li>
              ))}
            </ol>
          </nav>
        </div>
        <span className="copyright-site">© {new Date().getFullYear()} Lumora. {text.rights}</span>
        <div className="colophon"><LanguageSwitcher /></div>
      </footer>
    </div>
  );
};
const MobileViewHeader = () => {
  const config = getConfig();
  const intl = useIntl();
  const messages = {
    "mobile.view.header.logo.altText": {
      id: "mobile.view.header.logo.altText",
      defaultMessage: "Lumora",
      description: "alt text for the mobile view header logo",
    },
  };

  const BASE_URL = config.LMS_BASE_URL;

  return (
    <>
      <style>
        {`
          #root .logo-image.logo-white {
            display: none;
          }
          [data-paragon-theme-variant="dark"] #root .logo-image {
            display: none;
          }
          [data-paragon-theme-variant="dark"] #root .logo-white {
            display: block;
          }
        `}
      </style>
      <a href={`${BASE_URL}/dashboard`} title="Lumora" className="logo">
        <img className="logo-image" src={`${BASE_URL}/static/lumora/images/logo.png`} alt={intl.formatMessage(messages["mobile.view.header.logo.altText"])} />
        <img className="logo-image logo-white" src={`${BASE_URL}/static/lumora/images/logo-white.png`} alt={intl.formatMessage(messages["mobile.view.header.logo.altText"])} />
      </a>
    </>
  );
};
const ThemedLogo = () => {
  const BASE_URL = getConfig().LMS_BASE_URL;

  return (
    <>
      <style>
        {`
          #root header .logo-image.logo-white {
            display: none;
          }
          [data-paragon-theme-variant="dark"] #root header .logo-image {
            display: none;
          }
          [data-paragon-theme-variant="dark"] #root header .logo-white {
            display: block;
          }
        `}
      </style>
      <a href={`${BASE_URL}/dashboard`} title="Lumora" className="logo">
        <img className="logo-image" src={`${BASE_URL}/static/lumora/images/logo.png`} alt="Lumora" />
        <img className="logo-image logo-white" src={`${BASE_URL}/static/lumora/images/logo-white.png`} alt="Lumora" />
      </a>
    </>
  );
};
  addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
    {
        op: PLUGIN_OPERATIONS.Insert,
        widget: {
            id: 'lumora_footer',
            type: DIRECT_PLUGIN,
            priority: 1,
            RenderWidget: LumoraFooter,
        },
    },
]);
  addPlugins(config, 'desktop_secondary_menu_slot', [
    {
        op: PLUGIN_OPERATIONS.Insert,
        widget: {
            id: 'lumora_language_switch',
            priority: 1,
            type: DIRECT_PLUGIN,
            RenderWidget: LanguageSwitcher,
        },
    },
]);
  addPlugins(config, 'mobile_header_slot', [
    {
        op: PLUGIN_OPERATIONS.Hide,
        widgetId: 'default_contents',
    },
    {
        op: PLUGIN_OPERATIONS.Insert,
        widget: {
            id: 'theme_switch_button',
            type: DIRECT_PLUGIN,
            RenderWidget: MobileViewHeader,
        },
    },
]);
  addPlugins(config, 'logo_slot', [
    {
        op: PLUGIN_OPERATIONS.Hide,
        widgetId: 'default_contents',
    },
    {
        op: PLUGIN_OPERATIONS.Insert,
        widget: {
            id: 'custom_logo',
            type: DIRECT_PLUGIN,
            RenderWidget: ThemedLogo,
        }
    }
]);
if (config.pluginSlots && config.pluginSlots['logo_slot']) {
  config.pluginSlots['logo_slot'].keepDefault = false;

  const logoInsertPlugins =
    config.pluginSlots['logo_slot'].plugins.filter(plugin => plugin.op === 'insert' && plugin.widget?.id === 'custom_logo');

  if (logoInsertPlugins.length > 1) {
    let kept = false;
    config.pluginSlots['logo_slot'].plugins =
      config.pluginSlots['logo_slot'].plugins.filter(plugin => {
        if (plugin.op === 'insert' && plugin.widget?.id === 'custom_logo') {
          if (kept) return false;
          kept = true;
        }
        return true;
      });
  }
}

if (config.pluginSlots && config.pluginSlots['org.openedx.frontend.layout.footer.v1']) {
  const footerPluginsToInsert =
    config.pluginSlots['org.openedx.frontend.layout.footer.v1'].plugins.filter(plugin => plugin.op === 'insert');

  if (footerPluginsToInsert.length > 2) {
  config.pluginSlots['org.openedx.frontend.layout.footer.v1'].plugins =
    config.pluginSlots['org.openedx.frontend.layout.footer.v1'].plugins.filter(plugin => {
      if (plugin.op === 'insert') {
        const widgetId = plugin.widget?.id;
        return widgetId !== 'lumora_footer';
      }
      return true;
    });
  }
}

  return config;
}

export default setConfig;